import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import ComboFilters from "../../ComboFilters";
import {
  getGrowthPercentage,
  getLastSixYears,
  months,
  Trend,
} from "../../Reports";
import { ReportsAPI } from "providers/ReportsAPI";
import dayjs from "dayjs";
import { If } from "shared/components/control-statements";
import { Loading } from "@components";
import { Currency } from "shared/utils/Currency";

export const EngagementBehaviorChart = ({
  selectedData,
  onClose,
  startOn2025,
  isCashback,
}) => {
  const [loading, setLoading] = useState(true);
  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [data, setData] = useState({
    never_received: [],
    never_used: [],
    used_more_than_a_time: [],
    used_more_than_three_times: [],
    used_more_than_ten_times: [],
  });

  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState({
    name: currentYear,
    id: currentYear,
  });
  const [selectedFilter, setSelectedFilter] = useState({
    name: "Último pedido há",
    id: "recency",
  });

  const formatData = () => {
    const today = dayjs();

    if (selectedYear.id < today.year()) return data;

    const result = {};

    for (const category in data) {
      result[category] = {};

      for (const metric in data[category]) {
        const sliced = data[category][metric].slice(0, today.month() + 1);

        result[category][metric] = sliced.map((value) =>
          value === 0 ? null : value
        );
      }
    }

    return result;
  };

  const series = formatData();

  const roundValue = (v) => {
    return Math.round(v / 10) * 10;
  };

  const getVisibleSeriesMin = (selected) => {
    let min = Infinity;
    let max = -Infinity;

    const normalizeLabel = (label) => {
      const labels = {
        "Nunca ganhou cashback": "never_received",
        "Ganhou cashback e nunca usou": "never_used",
        "Usou ao menos 1 vez": "used_more_than_a_time",
        "Usou ao menos 3 vezes": "used_more_than_three_times",
        "Usou ao menos 10 vezes": "used_more_than_ten_times",
        never_received: "never_received",
        never_used: "never_used",
        used_more_than_a_time: "used_more_than_a_time",
        used_more_than_three_times: "used_more_than_three_times",
        used_more_than_ten_times: "used_more_than_ten_times",
      };

      return labels[label] || label;
    };

    for (const label in selected) {
      if (!selected[label]) continue;

      const key = normalizeLabel(label);

      const serieData = data[key][selectedFilter.id];

      if (!serieData) continue;

      const values = serieData
        .map((d) => Number(d))
        .filter((v) => !isNaN(v) && v !== 0);

      if (values.length === 0) continue;

      const localMin = Math.min(...values);
      const localMax = Math.max(...values);
      min = Math.min(min, localMin);
      max = Math.max(max, localMax);
    }

    if (!isFinite(min)) return 0;

    const padding = (max - min) * 0.2;
    const ajustado = Math.max(0, Math.floor(min - padding));
    const rounded = roundValue(ajustado);

    return rounded;
  };

  const handleLegendSelectChanged = (e) => {
    const instance = echartsRef.current.getEchartsInstance();
    const selected = e.selected;

    const newMin = getVisibleSeriesMin(selected);

    instance.setOption({
      yAxis: {
        startValue: newMin,
      },
    });
  };

  const buildOptions = () => {
    return {
      grid: {
        left: 10,
        right: 30,
        top: 30,
        bottom: 70,
        containLabel: true,
      },
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "none",
        },
        backgroundColor: "#2B2B2B",
        borderRadius: 8,
        padding: [8, 8],
        borderWidth: 0,
        textStyle: {
          color: "#FFF",
          fontSize: 10,
        },
        formatter: (params) => {
          const name = params.name;

          if (params.data === null) return;

          let formattedValue;

          switch (selectedFilter.id) {
            case "recency":
            case "frequency":
              formattedValue = `${Math.round(params.value).toLocaleString(
                "pt-BR"
              )} dias`;
              break;

            case "average_ticket":
            case "total":
              formattedValue = Currency.formatCurrency(params.value || 0);
              break;

            case "quantity":
              formattedValue = params.value?.toFixed(1).replace(".", ",");
              break;

            default:
              formattedValue = params.value;
              break;
          }

          return `
            <div>
              <strong>${name}</strong><br/><br/>
              ${params.marker} ${params.seriesName}: <b>${formattedValue}</b>
            </div>
          `;
        },
      },
      legend: {
        type: "scroll",
        orient: "horizontal",
        pageIconColor: "#6D6DF2",
        pageIconInactiveColor: "#4C4C4C",
        backgroundColor: "#1C1C1C",
        borderRadius: 10,
        padding: 20,
        pageIconSize: 10,
        pageTextStyle: {
          color: "#B3B3B3",
          fontSize: 10,
          fontWeight: 800,
        },
        pageIcons: {
          horizontal: [
            "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
            "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
          ],
        },
        top: "bottom",
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 10,
        textStyle: {
          color: "#808080",
          fontSize: 12,
        },
        inactiveColor: "#555",
      },
      xAxis: {
        type: "category",
        data: months,
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 1,
            type: "solid",
          },
        },
      },
      yAxis: {
        type: "value",
        startValue: getVisibleSeriesMin(
          Object.keys(data).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        ),
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        axisLabel: {
          formatter: (value) => `${value.toLocaleString("pt-BR")}`,
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 0,
            type: "solid",
          },
        },
      },
      series: [
        {
          name: isCashback ? "Nunca ganhou cashback" : "Nunca ganhou pontos",
          type: "line",
          data: series.never_received?.[selectedFilter.id],
          color: "#4bca97",
          symbolSize: 8,
          itemStyle: {
            color: "#4bca97",
            borderColor: "#4bca97",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#4bca97",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: isCashback
            ? "Ganhou cashback e nunca usou"
            : "Ganhou pontos e nunca usou",
          type: "line",
          data: series.never_used?.[selectedFilter.id],
          color: "#de52b4",
          symbolSize: 8,
          itemStyle: {
            color: "#de52b4",
            borderColor: "#de52b4",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#de52b4",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: isCashback
            ? "Usou ao menos 1 vez"
            : "Usou pontos ao menos 1 vez",
          type: "line",
          data: series.used_more_than_a_time?.[selectedFilter.id],
          color: "#d9d26c",
          symbolSize: 8,
          itemStyle: {
            color: "#d9d26c",
            borderColor: "#d9d26c",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#d9d26c",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: isCashback
            ? "Usou ao menos 3 vezes"
            : "Usou pontos ao menos 3 vezes",
          type: "line",
          data: series.used_more_than_three_times?.[selectedFilter.id],
          color: "#c74f53",
          symbolSize: 8,
          itemStyle: {
            color: "#c74f53",
            borderColor: "#c74f53",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#c74f53",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: isCashback
            ? "Usou ao menos 10 vezes"
            : "Usou pontos ao menos 10 vezes",
          type: "line",
          data: series.used_more_than_ten_times?.[selectedFilter.id],
          color: "#515d73",
          symbolSize: 8,
          itemStyle: {
            color: "#515d73",
            borderColor: "#515d73",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#515d73",
          },
          symbol: "circle",
          connectNulls: false,
        },
      ],
    };
  };
  const fetchHistory = async (payload) => {
    setLoadingHistoric(true);
    const { year } = payload;
    const provider = new ReportsAPI();
    const results =
      (await provider.loyaltyEngagementsHistory("behavior", year, null)) || [];

    const limit = 12;
    const rows = Array(limit).fill(null);

    if (!results || !results.length) {
      setLoadingHistoric(false);
      return rows;
    }

    for (const result of results) {
      if (!result?.month) continue;

      const format = "MM/YYYY";
      const date = dayjs(result.month, format);

      const index = date.month() + 1;

      if (index >= 1 && index <= limit) {
        const parsed = {};

        for (const status of Object.keys(result)) {
          if (status === "date") continue;

          const statusData = result[status];
          parsed[status] = {};

          for (const metric in statusData) {
            const metricData = statusData[metric];
            parsed[status][metric] = Number(metricData?.value ?? 0);
          }
        }

        rows[index - 1] = parsed;
      }
    }

    setLoadingHistoric(false);

    return rows;
  };

  const fetchData = async () => {
    setLoading(true);
    const payload = { year: selectedYear?.id };

    const results = await fetchHistory(payload);

    const statusKeys = [
      "never_received",
      "never_used",
      "used_more_than_a_time",
      "used_more_than_three_times",
      "used_more_than_ten_times",
    ];

    const metricKeys = ["recency", "frequency", "ticket", "orders", "total"];

    const series = {};
    for (const status of statusKeys) {
      series[status] = {};
      for (const metric of metricKeys) {
        series[status][metric] = [];
      }
    }

    results.forEach((result) => {
      for (const status of statusKeys) {
        for (const metric of metricKeys) {
          const value = result?.[status]?.[metric];
          series[status][metric].push(value !== undefined ? value : 0);
        }
      }
    });

    setData(series);
    setLoading(false);
  };

  const [options, setOptions] = useState(buildOptions());

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  useEffect(() => {
    setOptions(buildOptions());
  }, [selectedFilter, data]);

  const echartsRef = useRef(null);

  useEffect(() => {
    const chart = echartsRef.current?.getEchartsInstance();
    if (chart) {
      setTimeout(() => chart.resize(), 0);
    }
  }, []);

  return (
    <div className={styles.monthYearLegendChartContainer}>
      <div className={styles.monthYearHeader}>
        <p className={styles.title}>{selectedData?.name}</p>
        <div className="d-flex align-center">
          <div className={styles.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>

      <div className={styles.monthYearLegendContent}>
        <If cond={loadingHistoric}>
          <div className={styles.loading}>
            <Loading />
          </div>
        </If>

        <If cond={!loadingHistoric}>
          <div className={styles.legendContainer}>
            <div className={styles.header}>
              Crescimento no {selectedFilter === "year" ? "Ano" : "Mês"}
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#4bca97" }} />
                <p className={styles.name}>
                  {isCashback ? "Nunca ganhou cashback" : "Nunca ganhou pontos"}
                </p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.never_received[selectedFilter.id],
                  selectedFilter,
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#de52b4" }} />
                <p className={styles.name}>
                  {isCashback
                    ? "Ganhou cashback e nunca usou"
                    : "Ganhou pontos e nunca usou"}
                </p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.never_used[selectedFilter.id],
                  selectedFilter,
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#d9d26c" }} />
                <p className={styles.name}>
                  {isCashback
                    ? "Usou ao menos 1 vez"
                    : "Usou pontos ao menos 1 vez"}
                </p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.used_more_than_a_time[selectedFilter.id],
                  selectedFilter,
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#c74f53" }} />
                <p className={styles.name}>
                  {isCashback
                    ? "Usou ao menos 3 vezes"
                    : "Usou pontos ao menos 3 vezes"}
                </p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.used_more_than_three_times[selectedFilter.id],
                  selectedFilter,
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#515d73" }} />
                <p className={styles.name}>
                  {isCashback
                    ? "Usou ao menos 10 vezes"
                    : "Usou pontos ao menos 10 vezes"}
                </p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.used_more_than_ten_times[selectedFilter.id],
                  selectedFilter,
                  selectedYear,
                })}
              />
            </div>
          </div>

          <div className={styles.chartContainer}>
            <div className={styles.filters}>
              <div className="d-flex align-center gap-10">
                <ComboFilters
                  options={getLastSixYears(startOn2025)}
                  onSelect={(option) => setSelectedYear(option)}
                  selectedOption={selectedYear}
                />
                <ComboFilters
                  options={[
                    { name: "Último pedido há", id: "recency" },
                    {
                      name: "Intervalo entre compras",
                      id: "frequency",
                    },
                    {
                      name: "Ticket médio",
                      id: "ticket",
                    },
                    {
                      name: "Pedidos por cliente",
                      id: "orders",
                    },
                    {
                      name: "Faturamento médio",
                      id: "total",
                    },
                  ]}
                  onSelect={(option) => setSelectedFilter(option)}
                  selectedOption={selectedFilter}
                />
              </div>
            </div>
            <div className={styles.monthYearChart}>
              <If cond={!loading}>
                <ReactECharts
                  ref={echartsRef}
                  option={options}
                  style={{ width: "100%", height: "100%" }}
                  notMerge={true}
                  lazyUpdate={true}
                  opts={{ renderer: "canvas" }}
                  onEvents={{
                    legendselectchanged: handleLegendSelectChanged,
                    // mouseover: handleClick,
                  }}
                />
              </If>
            </div>
          </div>
        </If>
      </div>
    </div>
  );
};
